import { Component, EventEmitter, Input, OnInit, Output, Self, Optional, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'rfr-input-phone',
  templateUrl: './rfr-input-phone.component.html',
  styleUrls: ['./rfr-input-phone.component.scss'],
})
export class RfrInputPhoneComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() type: string = 'text';
  @Input() alignInputText: 'center' | 'left' | 'right' = 'left';
  @Input() labelText: string = '';
  @Input() placeholder: string = '';
  @Input() pattern: string = '';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage: string = '';
  @Input() min!: number;
  @Input() max!: number;
  @Input() maxLength: number = 255;
  @Input() class = "";
  @Input() style = "";
  @Input() size: 'normal' | 'large' = 'normal'; // Default value is 'normal'
  @Input() dotNumber: number = 2;
  @Input() code: string = '+60';
  @Input() country: string = 'Malaysia';
  @Output() blur: EventEmitter<void> = new EventEmitter<void>();
  numberCode: any;
  numbers: any[] = [];
  displayValue: string = '';
  disabled!: boolean;
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };
  private _lastWriteValue: any = null;


  constructor(
    @Self() @Optional() public controlDir: NgControl,
    private http: HttpClient
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
    
  }

  async ngOnInit() {
    const data = await this.http.get<any[]>('assets/phone-flags/countries-phone-codes.json').toPromise();
    if(data)
      this.numbers = data.sort((a, b) => a.id <b.id ? -1 : 1);

    this.numberCode = this.resolveNumberCode(this._lastWriteValue);

    if (this.controlDir) {
      const control = this.controlDir.control;
      if (this.required && control) {
        const validators = control.validator
          ? [control.validator, Validators.required]
          : Validators.required;

        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    }
  }


  writeValue(value: any): void {
    if (this.displayValue != value) {
      this._lastWriteValue = value;
      const convertedValue = this.convertPhoneNumber(value);
      this.displayValue = convertedValue?.value;
      if (this.numbers && this.numbers.length > 0) {
        this.setNumberCodeFromValue(value);
        // If emnpty use Malaysia as default
        // if (!convertedValue?.value && !this.numberCode) {
        //   this.numberCode = this.numbers.find(x => x.code === this.code) || this.numbers[0];
        // }
      }
    }
  }

  private setNumberCodeFromValue(value: any) {
    this.numberCode = this.resolveNumberCode(value);
  }

  /**
   * Resolves the dial-code entry to preselect.
   * 1. An explicit dial code inside the value ("+65-123") always wins.
   * 2. Otherwise the `country` name (the stored countryPhoneNumber) decides.
   * 3. Otherwise fall back to the `code` input, then the first entry.
   */
  private resolveNumberCode(value: any): any {
    if (!this.numbers || !this.numbers.length) return this.numberCode;

    const explicitCode = this.convertPhoneNumber(value)?.code || null;

    if (explicitCode) {
      return this.numbers.find(x => x.code == explicitCode && (!this.country || x.country === this.country))
          || this.numbers.find(x => x.code == explicitCode)
          || this.numbers.find(x => x.code === this.code)
          || this.numbers[0];
    }

    if (this.country) {
      const byCountry = this.numbers.find(x => x.country === this.country);
      if (byCountry) return byCountry;
    }

    return this.numbers.find(x => x.code === this.code) || this.numbers[0];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['country'] || changes['code']) && this.numbers && this.numbers.length) {
      this.numberCode = this.resolveNumberCode(this._lastWriteValue);
    }
  }

  registerOnChange(onChange: (value: any) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onBlur(): void {
    this.onTouched(); // Manually trigger the onTouched method
    this.blur.emit(); // Emit the blur event
  }

  onChangeInput(value: any): void {
    // console.log(value);
    const numberValue = value.replace(/\D/g, '');
    let res = numberValue;
    if (value == this.numberCode?.code || value == `${this.numberCode?.code}-` || !value) {
      res = ""
    } else {
      res = `${this.numberCode?.code}-${res}`;
    }
    this.displayValue = numberValue;
    this.onChange(res);
  }
  allowNumber(event: any) {
  }

  handleChange(e: any) {
    console.log('rfr-input-phone.handleChange - ionChange fired with value:', e.detail.value);
    this.numberCode = e.detail.value;
    const numberValue = this.displayValue?.replace(/\D/g, '') || '';

    let valueToEmit = '';
    if (numberValue) {
      valueToEmit = `${this.numberCode.code}-${numberValue}`;
    }
    this.onChange(valueToEmit);
  }

  handleCancel() {
    console.log('ionCancel fired');
  }

  handleDismiss() {
    console.log('ionDismiss fired');
  }

  private convertPhoneNumber(phone: any) {
    if (!phone) return { code: null, country: null, value: '' };
    const splits = phone.split('-');

    if (splits.length >= 2) {
      return { code: splits[0], country: null, value: splits.slice(1).join('-') };
    }

    // No explicit dial code embedded in the value.
    return { code: null, country: null, value: splits[0] };
  }

}
